// Identifier-first login: the user enters their email, we ask the backend
// which identity provider their Organization is configured for (see
// api/view/auth_views.py::resolve_identity_provider), then redirect to
// that provider. Defaults to Auth0 for any email we can't resolve -
// Keycloak is currently local-dev-only (see
// docker-compose.exp-core-keycloak.yml).
//
// Layout mirrors the branded login templates used by the upstream
// providers themselves as closely as this templating system allows -
// terraform/auth0/auth0-actions/templates/brand2026_login.html (Liquid) and
// exp-core's keycloak/themes/confirm/login/ (FreeMarker) - same
// art panel SVG, same logo, same widget layout/measurements, so all three
// read as one page regardless of which provider a given visit lands on.

import { useAuth0 } from '@auth0/auth0-react';
import React, { FC, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Button, Form, FormGroup, Input, Label } from 'reactstrap';
import logoWithName2026 from '../../assets/img/brand/logo_2026.svg';
import { URL_QUERY_PARAMETERS } from '../../consts/consts';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import { KeycloakAuthContext } from '../../utils/api/KeycloakAuthContext';
import {
  isKeycloakEnabled,
  setActiveAuthProvider,
  toKeycloakClientConfig,
} from '../../utils/api/keycloakClient';
import { useAuthProvider } from '../../utils/api/useAuthProvider';
import { redirectLoginOptionsGenerator } from '../../utils/util/utiltsx';
import Loading from '../Widgets/Loading';

type IdentityProvider = 'auth0' | 'keycloak';

// /api/auth/resolve-provider's answer (api/view/auth_views.py). For
// keycloak, `keycloak` is the public config of the client the
// organization's users log in through - the deploy's default, or the
// organization's own Keycloak instance (see utils/api/keycloakClient.ts).
interface ResolvedIdentityProvider {
  identity_provider: IdentityProvider;
  keycloak?: {
    issuer: string;
    url: string;
    realm: string;
    client_id: string;
  };
}

// Scoped to this page only (not global SCSS) - matches the self-contained
// <style> blocks in the Auth0/Keycloak templates it mirrors.
const artStyles = `
  .identifier-first-login-shell {
    display: flex;
    min-height: 100vh;
    background: #ffffff;
  }
  .identifier-first-login-art {
    flex: 0 0 30%;
    max-width: 400px;
    margin: 20px;
    border-radius: 24px;
    overflow: hidden;
    position: relative;
  }
  .identifier-first-login-art svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
  .identifier-first-login-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .identifier-first-login-widget {
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 12vh;
    width: 100%;
  }
  .identifier-first-login-form {
    width: 100%;
    max-width: 360px;
    padding: 0 20px;
  }
  .identifier-first-login-logo {
    max-height: 1.6rem;
    max-width: 7.5rem;
    margin-bottom: 24px;
  }
  @media (max-width: 900px) {
    .identifier-first-login-art { display: none; }
    .identifier-first-login-widget { padding-top: 0; align-items: center; }
  }
`;

// Same abstract shape as brand2026_login.html's .brand2026-art - keep the
// two in sync if that one ever changes.
const ArtPanel: FC = () => (
  <div className="identifier-first-login-art" aria-hidden="true">
    <svg
      viewBox="0 0 616 1176"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="616" height="1176" fill="#FEF678" />
      <path
        d="M0 466 L8 453 L16 436 L24 420 L32 405 L40 395 L48 393 L56 393 L64 393 L72 393 L80 393 L88 393 L96 393 L104 393 L112 393 L120 393 L128 393 L136 393 L144 396 L152 408 L160 423 L168 438 L176 455 L184 473 L192 492 L200 513 L208 513 L216 472 L224 432 L232 398 L240 368 L248 341 L256 316 L264 294 L272 273 L280 254 L288 236 L296 219 L304 203 L312 188 L320 175 L328 167 L336 166 L344 166 L352 166 L360 166 L368 166 L376 166 L384 166 L392 166 L400 166 L408 166 L416 166 L424 166 L432 166 L440 166 L448 166 L456 167 L464 174 L472 188 L480 202 L488 217 L496 233 L504 250 L512 268 L520 288 L528 308 L536 330 L544 353 L552 352 L560 298 L568 247 L576 204 L584 168 L592 135 L600 105 L608 77 L615 59 L615 949 L608 931 L600 904 L592 874 L584 847 L576 861 L568 900 L560 934 L552 963 L544 989 L536 1013 L528 1035 L520 1056 L512 1075 L504 1093 L496 1111 L488 1126 L480 1142 L472 1156 L464 1169 L456 1176 L448 1176 L440 1176 L432 1176 L424 1176 L416 1176 L408 1176 L400 1176 L392 1176 L384 1176 L376 1176 L368 1176 L360 1176 L352 1176 L344 1176 L336 1176 L328 1176 L320 1168 L312 1155 L304 1140 L296 1124 L288 1108 L280 1090 L272 1071 L264 1050 L256 1028 L248 1003 L240 977 L232 957 L224 977 L216 1007 L208 1034 L200 1058 L192 1079 L184 1098 L176 1116 L168 1133 L160 1148 L152 1162 L144 1173 L136 1176 L128 1176 L120 1176 L112 1176 L104 1176 L96 1176 L88 1176 L80 1176 L72 1176 L64 1176 L56 1176 L48 1176 L40 1175 L32 1165 L24 1151 L16 1135 L8 1118 L0 1104 Z"
        fill="#F9DA4B"
      />
    </svg>
  </div>
);

const IdentifierFirstLogin: FC = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const auth0 = useAuth0();
  const keycloakAuth = useContext(KeycloakAuthContext);
  // Whichever provider the last session used - to redirect away if the
  // user is already authenticated and lands here anyway.
  const { isAuthenticated, isLoading } = useAuthProvider();

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const continueUrl =
    new URLSearchParams(location.search).get(
      URL_QUERY_PARAMETERS.CONTINUE_URL
    ) || '/';
  const isSignup = continueUrl.startsWith('/signup');

  const redirectToProvider = async (
    resolved: ResolvedIdentityProvider,
    loginHint: string
  ) => {
    const options = redirectLoginOptionsGenerator(continueUrl, isSignup);

    if (resolved.identity_provider === 'keycloak') {
      // Rejects (-> the generic error below) on an incomplete config -
      // a backend misconfiguration, not something the user can fix by
      // retrying.
      const client = toKeycloakClientConfig(resolved.keycloak);
      if (resolved.keycloak && !client) {
        throw new Error('Incomplete Keycloak client config from the backend.');
      }
      await keycloakAuth.loginWithRedirect({
        appState: options.appState,
        loginHint,
        client,
      });
      return;
    }

    setActiveAuthProvider('auth0');
    await auth0.loginWithRedirect({ ...options, login_hint: loginHint });
  };

  // Defensive: if someone bookmarked/typed this URL directly while
  // Keycloak/this page is disabled (see keycloakClient.ts), don't show the
  // picker - go straight to Auth0, the same fallback the gates that would
  // normally send someone here (App.tsx, ProtectedRoute.tsx) already use.
  // Computed/used before any early return so the hook below always runs.
  const keycloakEnabled = isKeycloakEnabled();
  useEffect(() => {
    if (isLoading || isAuthenticated || keycloakEnabled) {
      return;
    }
    auth0.loginWithRedirect(
      redirectLoginOptionsGenerator(continueUrl, isSignup)
    );
  }, [isLoading, isAuthenticated, keycloakEnabled, auth0.loginWithRedirect]);

  if (isLoading) {
    // Don't flash the email form while the auth SDK is still restoring a
    // possible existing session (Auth0 localstorage cache / Keycloak's
    // silent check-sso iframe) - the redirect below may be about to fire.
    // Same spinner + message App.tsx shows for this same wait.
    return (
      <Loading
        message={formatMessage({
          id: 'app.app.message.initializing',
          defaultMessage: 'Initializing...',
        })}
      />
    );
  }

  if (isAuthenticated) {
    // Already logged in (e.g. a stale tab) - go straight to the
    // destination rather than showing the picker again.
    window.location.href = continueUrl;
    return null;
  }

  if (!keycloakEnabled) {
    return null;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || !email) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await ConfirmAPI.post('/auth/resolve-provider', {
        email,
      });
      await redirectToProvider(
        response.data as ResolvedIdentityProvider,
        email
      );
    } catch (e) {
      console.error(e);
      setIsSubmitting(false);
      setErrorMessage(
        formatMessage({
          id: 'app.login.identifierFirst.error',
          defaultMessage:
            'Something went wrong. Please check your email address and try again.',
        })
      );
    }
  };

  return (
    <div className="identifier-first-login-shell">
      <style>{artStyles}</style>
      <ArtPanel />
      <div className="identifier-first-login-main">
        <div className="identifier-first-login-widget">
          <div className="identifier-first-login-form">
            <img
              className="identifier-first-login-logo"
              src={logoWithName2026}
              alt="Confirm Logo"
            />
            <h1 className="display-4 mb-3">
              <FormattedMessage
                id="app.login.identifierFirst.title"
                defaultMessage="Log in to Confirm"
              />
            </h1>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="identifier-first-login-email">
                  <FormattedMessage
                    id="app.login.identifierFirst.emailLabel"
                    defaultMessage="Work email"
                  />
                </Label>
                <Input
                  id="identifier-first-login-email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormGroup>
              {errorMessage && <p className="text-danger">{errorMessage}</p>}
              <Button color="primary" type="submit" disabled={isSubmitting}>
                <FormattedMessage
                  id="app.login.identifierFirst.continue"
                  defaultMessage="Continue"
                />
              </Button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(IdentifierFirstLogin);
